import Decimal from 'decimal.js';
import { DateTime } from 'luxon';
import { unparse } from 'papaparse';
import { IntlShape } from 'react-intl';

import { AggregatedPurchasedItemOptionValue } from 'apis/merchant-gateway/merchant-analytics/types';
import { Purchase } from 'apis/restaurant-api/merchant-admin/purchases/types';

const convertMonetaryStringToInt = (amount: string, currency: Currency) => {
  return parseFloat(amount).toFixed(currency === 'JPY' ? 0 : 2);
};

export const toByDateCSV = (purchases: Purchase[], timezone: string, intl: IntlShape) =>
  unparse(
    purchases.map(purchase => ({
      [intl.formatMessage({ id: 'purchases.csv.order-placed' })]: DateTime.fromISO(purchase.created_at)
        .setZone(timezone)
        .toFormat('dd.MM.yyyy HH:mm'),
      [intl.formatMessage({ id: 'purchases.csv.order-number' })]: `#${purchase.order_number}`,
      [intl.formatMessage({ id: 'purchases.csv.delivery-status' })]: purchase.delivery.status,
      [intl.formatMessage({ id: 'purchases.csv.delivery-time' })]:
        purchase.delivery.time !== null
          ? DateTime.fromISO(purchase.delivery.time).setZone(timezone).toFormat('dd.MM.yyyy HH:mm')
          : '–',
      [intl.formatMessage({ id: 'purchases.csv.delivery-type' })]: purchase.delivery.type,
      [intl.formatMessage({ id: 'purchases.csv.items' })]: purchase.items
        .map(
          item =>
            `${item.count}x ${item.name} á ${convertMonetaryStringToInt(
              item.unit_price.amount,
              item.unit_price.currency,
            )}`,
        )
        .join(', '),
      [intl.formatMessage({ id: 'purchases.csv.pos-ids' })]: purchase.items
        .filter(item => !!item.external_id)
        .map(item => `${item.count}x ${item.external_id}`)
        .join(', '),
      [intl.formatMessage({ id: 'purchases.csv.price' })]: convertMonetaryStringToInt(
        purchase.price.amount,
        purchase.price.currency,
      ),
      [intl.formatMessage({ id: 'purchases.csv.currency' })]: purchase.price.currency,
      [intl.formatMessage({ id: 'purchases.csv.review-score' })]: purchase.review?.score,
      [intl.formatMessage({ id: 'purchases.csv.review-comment' })]: purchase.review?.comment,
      [intl.formatMessage({ id: 'purchases.csv.review-attributions' })]: purchase.review
        ? purchase.review.attributions.join(', ')
        : '',
      [intl.formatMessage({ id: 'purchases.csv.venue' })]: purchase.restaurant.name,
    })),
  );

type ItemData = {
  id: string;
  image: string | null;
  name: string;
  count: number;
  weightInGrams: number | null;
  price: { amount: string; currency: Currency };
  barcode: string | null;
};

export function aggregateItemDataFromPurchases(purchases: Purchase[]) {
  const allItemsData = purchases
    .sort((a, b) => new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf())
    .flatMap(purchase =>
      purchase.items.map(item => ({
        id: item.id,
        image: null,
        name: item.name,
        count: item.count,
        weightInGrams: item.weighted_item_info ? item.weighted_item_info.purchased_weight_in_grams : null,
        price: item.total_price,
        barcode: item.barcode_gtin,
      })),
    );
  const aggregatedDataIndexedById = allItemsData.reduce<Record<string, ItemData>>((acc, curr) => {
    if (acc[curr.id]) {
      const previousCount = acc[curr.id].count;
      const previousWeightInGrams = acc[curr.id].weightInGrams;
      const previousPrice = acc[curr.id].price;
      const aggregatedItem = {
        ...curr,
        count: previousCount + curr.count,
        weightInGrams:
          previousWeightInGrams && curr.weightInGrams
            ? previousWeightInGrams + curr.weightInGrams
            : previousWeightInGrams,
        price: {
          amount: new Decimal(previousPrice.amount).add(new Decimal(curr.price.amount)).toString(),
          currency: curr.price.currency,
        },
      };
      return { ...acc, [curr.id]: aggregatedItem };
    }
    return { ...acc, [curr.id]: curr };
  }, {});
  return Object.values(aggregatedDataIndexedById || {});
}

export const deliveryFeesIncludeServiceFees = (country: Country): boolean => {
  const countriesWithServiceFeesEnabled = ['CZE', 'GEO', 'POL'];
  return countriesWithServiceFeesEnabled.includes(country);
};

export const isIsoDate = (str: string) => {
  const dateTime = DateTime.fromISO(str);
  return dateTime.isValid && dateTime.toISODate() === str;
};

export const isValidPeriod = (period: string) => {
  if (['today', 'yesterday', 'this-week', 'last-week', 'this-month', 'last-month'].includes(period)) {
    return true;
  }

  const [from, to] = period.split('--');
  if (from && to && isIsoDate(from) && isIsoDate(to)) {
    return true;
  }

  return false;
};

export const periodToIsoDateRange = (timezone: string, nowMillis: number, period: string) => {
  if (!isValidPeriod(period)) {
    return undefined;
  }

  const now = DateTime.fromMillis(nowMillis, { zone: timezone });

  switch (period) {
    case 'today': {
      const today = now.startOf('day');
      return {
        from: today.toISODate(),
        to: today.toISODate(),
      };
    }
    case 'yesterday': {
      const yesterday = now.startOf('day').minus({ days: 1 });
      return {
        from: yesterday.toISODate(),
        to: yesterday.toISODate(),
      };
    }
    case 'this-week': {
      const startOfThisWeek = now.startOf('week');
      return {
        from: startOfThisWeek.toISODate(),
        to: startOfThisWeek.plus({ days: 6 }).toISODate(),
      };
    }
    case 'last-week': {
      const startOfLastWeek = now.startOf('week').minus({ days: 7 });
      return {
        from: startOfLastWeek.toISODate(),
        to: startOfLastWeek.plus({ days: 6 }).toISODate(),
      };
    }
    case 'this-month': {
      const startOfThisMonth = now.startOf('month');
      return {
        from: startOfThisMonth.toISODate(),
        to: startOfThisMonth.plus({ months: 1 }).minus({ days: 1 }).toISODate(),
      };
    }
    case 'last-month': {
      const startOfLastMonth = now.startOf('month').minus({ months: 1 });
      return {
        from: startOfLastMonth.toISODate(),
        to: startOfLastMonth.plus({ months: 1 }).minus({ days: 1 }).toISODate(),
      };
    }
  }

  const [from, to] = period.split('--');
  return { from, to };
};

export const getOptionsTotalPrice = (itemOptions: AggregatedPurchasedItemOptionValue[] = []) =>
  itemOptions.reduce(
    (total: number, optionValue: AggregatedPurchasedItemOptionValue) =>
      total + optionValue.pricing.optionOnlyPrice.gross,
    0,
  );
